"use client";

import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { Box } from "../../components/ui/box";
import type { FillOption } from "../../components/ui/box-classes";
import { Button, buttonVariants } from "../../components/ui/button";
import { Popover, PopoverPopup } from "../../components/ui/popover";
import { Text } from "../../components/ui/text";
import type { Color } from "../../lib/colors";
import { cn } from "../../lib/utils";
import type { Omittable } from "../../lib/props";

/**
 * Headless interaction shell for entity tags/chips: trigger + hover-intent
 * timers + lazy popover. `as="text"` renders an inline `Text` so a label-only
 * tag can sit inside a sentence. Until the first pointer-enter only the bare
 * trigger renders (no popover machinery) so virtualized tables of chips cost
 * nothing. Entity-agnostic by contract: kinds, labels, and data live in the
 * consumer's wrappers.
 */

const DEFAULT_OPEN_DELAY = 800;
const DEFAULT_CLOSE_DELAY = 200;

// Matches the Button `ghost` idiom so clickable tags read as clickable.
const GHOST_HOVER_BG =
  "transition-colors hover:bg-foreground/5 active:bg-foreground/5 dark:hover:bg-foreground/10 dark:active:bg-foreground/10";

// Layout-neutral: `p-1` bleeds the wash past the content, `-m-1` cancels it in
// layout so the footprint stays exactly the content and nothing shifts.
const GHOST_BOX_CLASSES = cn("rounded-sm", GHOST_HOVER_BG, "p-1 -m-1");

// Inline variant: vertical padding paints the bg without growing the line box,
// so only horizontal padding needs cancelling; clone repaints on wrapped lines.
const GHOST_TEXT_CLASSES = cn(
  "rounded-sm",
  GHOST_HOVER_BG,
  "px-1 py-0.5 -mx-1 [box-decoration-break:clone]",
);

export const entityChipVariants = cva("", {
  variants: {
    variant: {
      plain: "",
      chip: "rounded-sm",
    },
    // Caller gates `true` on `!!onClick && ghost` so a non-interactive chip never looks clickable.
    ghost: {
      true: GHOST_BOX_CLASSES,
      false: "",
    },
  },
  defaultVariants: {
    variant: "plain",
  },
});

type EntityChipShellVariantProps = VariantProps<typeof entityChipVariants>;

interface HoverIntentHandlers {
  onPointerEnter: () => void;
  onPointerLeave: () => void;
  /** Gated on a real move so a chip sliding under a stationary cursor never auto-opens. */
  onPointerMove: () => void;
}

interface HoverIntentState {
  open: boolean;
  /** Flips true on first pointer-enter and stays true (lazy-mount gate). */
  hasHovered: boolean;
  setOpen: (open: boolean) => void;
  triggerHandlers: HoverIntentHandlers;
  contentHandlers: HoverIntentHandlers;
  /** Pin open (a descendant hover popover took the pointer). */
  hold: () => void;
  release: () => void;
}

/**
 * Lets a nested hover popover keep its ancestors open: a child holds() its
 * ancestor while its own popover is open. Without it, moving into a popover the
 * card spawned (portaled out of its DOM) fires the card's leave and unmounts it.
 */
const HoverIntentHoldContext = React.createContext<{
  hold: () => void;
  release: () => void;
} | null>(null);

/** True inside a preview popup: a chip there opening its own preview must not
 *  close the popup it lives in. */
const InsidePreviewPopupContext = React.createContext(false);

/**
 * Every open preview's close function, in opening order. Chips are otherwise
 * fully independent — each closes only through its own grace timer — so
 * without this, a pointer sweeping a column of chips strands one preview open
 * per chip. Opening a preview closes every one already open, EXCEPT when the
 * new preview was spawned from inside another preview's popup: the pointer
 * traveled through that ancestor to get here, so it stays.
 */
const openPreviews: (() => void)[] = [];

function claimPreviewSlot(close: () => void, nested: boolean): void {
  // `close` itself is never in the stack here: a claim only runs on the
  // not-showing → showing transition, and the effect's cleanup released any
  // earlier claim before this one runs.
  if (!nested) {
    for (const other of openPreviews.splice(0)) other();
  }
  openPreviews.push(close);
}

function releasePreviewSlot(close: () => void): void {
  const index = openPreviews.indexOf(close);
  if (index !== -1) openPreviews.splice(index, 1);
}

/**
 * Synthetic events cross a portal along the REACT tree, not the DOM tree: an
 * unstopped click inside the (portaled) popup bubbles into whatever wraps the
 * chip — e.g. a selector's popover trigger, which then opens its picker behind
 * whatever the popup's row just opened. Native listeners outside the portal
 * never see popup events, so stopping the synthetic ones removes only the
 * leak. Base UI merges these with its own popup handlers, which still run.
 */
function stopSyntheticPropagation(e: React.SyntheticEvent): void {
  e.stopPropagation();
}

const POPUP_EVENT_SEAL = {
  onClick: stopSyntheticPropagation,
  onMouseDown: stopSyntheticPropagation,
  onPointerDown: stopSyntheticPropagation,
  onKeyDown: stopSyntheticPropagation,
  onKeyUp: stopSyntheticPropagation,
} as const;

/**
 * Hover-intent timers for lazy hover popovers (open after `openDelay`, close
 * after the `closeDelay` grace). Timers clear on unmount so virtualized rows
 * never leak.
 */
export function useHoverIntent(
  openDelay: number = DEFAULT_OPEN_DELAY,
  closeDelay: number = DEFAULT_CLOSE_DELAY,
  /** When false every handler is a no-op and no timer arms — for chips that
   *  can't hover, so non-hover chips in a list run zero machinery. */
  enabled: boolean,
): HoverIntentState {
  const [open, setOpenState] = React.useState(false);
  const [hasHovered, setHasHovered] = React.useState(false);
  // clearTimeout on undefined is a no-op, so clears never need a guard.
  const openTimer = React.useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const closeTimer = React.useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  // pointermove only arms the open timer while inside — guards a stray move racing in after leave.
  const insideRef = React.useRef(false);
  // Descendant popovers holding this one open; while > 0, a leave never schedules a close.
  const heldRef = React.useRef(0);

  const clearTimers = React.useCallback(() => {
    clearTimeout(openTimer.current);
    clearTimeout(closeTimer.current);
    openTimer.current = undefined;
    closeTimer.current = undefined;
  }, []);

  const setOpen = React.useCallback(
    (next: boolean) => {
      clearTimers();
      setOpenState(next);
    },
    [clearTimers],
  );

  const handleEnter = React.useCallback(() => {
    if (!enabled) return;
    setHasHovered(true);
    insideRef.current = true;
    clearTimeout(closeTimer.current);
    closeTimer.current = undefined;
    // Don't arm the open timer here: pointerenter also fires when the element
    // slides under a stationary cursor (text reflow). Wait for a real move.
  }, [enabled]);

  const handleMove = React.useCallback(() => {
    if (!enabled) return;
    if (!insideRef.current || openTimer.current) return;
    openTimer.current = setTimeout(() => {
      openTimer.current = undefined;
      setOpenState(true);
    }, openDelay);
  }, [openDelay, enabled]);

  const scheduleClose = React.useCallback(() => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => {
      closeTimer.current = undefined;
      setOpenState(false);
    }, closeDelay);
  }, [closeDelay]);

  const handleLeave = React.useCallback(() => {
    insideRef.current = false;
    clearTimeout(openTimer.current);
    openTimer.current = undefined;
    // A descendant popover owns the pointer — its release() schedules our close.
    if (heldRef.current > 0) return;
    scheduleClose();
  }, [scheduleClose]);

  const hold = React.useCallback(() => {
    if (!enabled) return;
    heldRef.current += 1;
    clearTimeout(closeTimer.current);
    closeTimer.current = undefined;
  }, [enabled]);

  const release = React.useCallback(() => {
    heldRef.current = Math.max(0, heldRef.current - 1);
    if (heldRef.current === 0) scheduleClose();
  }, [scheduleClose]);

  // Disabling must also discard in-flight state: an armed open timer would
  // still fire (handlers only gate NEW events), and a stale `open` would
  // re-show the popover the instant the chip is re-enabled.
  React.useEffect(() => {
    if (enabled) return;
    clearTimers();
    insideRef.current = false;
    setOpenState(false);
  }, [enabled, clearTimers]);

  React.useEffect(() => clearTimers, [clearTimers]);

  const handlers = React.useMemo<HoverIntentHandlers>(
    () => ({
      onPointerEnter: handleEnter,
      onPointerLeave: handleLeave,
      onPointerMove: handleMove,
    }),
    [handleEnter, handleLeave, handleMove],
  );

  return {
    open,
    hasHovered,
    setOpen,
    triggerHandlers: handlers,
    contentHandlers: handlers,
    hold,
    release,
  };
}

/** Opens an anchored confirm popover; only Confirm fires `onClick`. */
type ClickConfirmation = Omittable<{
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
}>;

export type EntityChipShellProps = EntityChipShellVariantProps & Omittable<{
  children: React.ReactNode;
  /** Tints the label. Applies in `chip` variant and in `as="text"` mode; a
   *  plain box chip stays untinted. The chip itself never fills. */
  color?: Color;
  /** `"box"` (default) = clickable row. `"text"` = inline Text so a label-only
   *  tag can sit inside a sentence; then `variant` is ignored. */
  as?: "box" | "text";
  /** Lazy hover-card slot — invoked only while the popover is open. */
  renderPreview?: () => React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  /** Click toggles the preview popover (only when `onClick` is absent). For
   *  chips whose preview is their only affordance — hover never fires on touch
   *  devices, so without this the preview is unreachable there. */
  clickOpensPreview?: boolean;
  confirmOnClick?: ClickConfirmation;
  /** Suppress hover + click entirely. */
  disabled?: boolean;
  /** Suppress the hover preview only (click still works). */
  disableHover?: boolean;
  openDelay?: number;
  closeDelay?: number;
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
  /** Fill available space (box mode only). */
  fill?: FillOption;
  /** Paint the ghost hover background (default true); only shows when interactive. */
  ghost?: boolean;
  /**
   * Render the box-mode trigger with a Button's exact ghost geometry at this
   * size (via `buttonVariants` classes on a Box — a non-`<button>`, so it
   * still nests inside clickable rows/anchors), for where a chip must line up
   * with adjacent buttons. Omit for the default content-sized, layout-neutral
   * ghost chip.
   */
  size?: VariantProps<typeof buttonVariants>["size"];
  /** Visual↔label gap. `'xs'` for the small chip sizes so the gap stays
   *  proportional to the glyph — derived by the entity wrappers from their
   *  size prop, never set per call site. Default `'sm'`. */
  visualGap?: "sm" | "xs";
}>;

function EntityChipShellComponent({
  children,
  variant = "plain",
  color,
  as = "box",
  renderPreview,
  onClick,
  clickOpensPreview,
  confirmOnClick,
  disabled,
  disableHover,
  openDelay,
  closeDelay,
  side = "top",
  align = "start",
  fill,
  ghost = true,
  size,
  visualGap = "sm",
}: EntityChipShellProps): React.ReactElement {
  const canHover = !disabled && !disableHover && !!renderPreview;
  const {
    open,
    hasHovered,
    setOpen,
    triggerHandlers,
    contentHandlers,
    hold,
    release,
  } = useHoverIntent(openDelay, closeDelay, canHover);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [clickOpen, setClickOpen] = React.useState(false);
  const triggerRef = React.useRef<HTMLElement | null>(null);
  const clickToPreview =
    !!clickOpensPreview && !disabled && !onClick && !!renderPreview;
  // The chip is the popover's ANCHOR, not its trigger, so a second press on it
  // reads to Base UI as an outside press — which closes the popup on the
  // pointer half, before our own click handler runs. Without this latch that
  // handler would toggle the freshly-closed preview straight back open and the
  // chip could never be clicked shut.
  const dismissedByChipRef = React.useRef(false);

  // While showing, hold our ancestor open so moving from its card into our
  // (portaled) popover doesn't trip its close timer; cleanup releases.
  const parentHold = React.useContext(HoverIntentHoldContext);
  const isShowing = open || confirmOpen || clickOpen;
  React.useEffect(() => {
    if (!parentHold || !isShowing) return;
    parentHold.hold();
    return () => parentHold.release();
  }, [parentHold, isShowing]);

  // One preview at a time, app-wide. The confirm popover is exempt: it exists
  // to answer a click just made, and a dwell elsewhere must not dismiss it.
  const insidePreviewPopup = React.useContext(InsidePreviewPopupContext);
  const previewShowing = open || clickOpen;
  const forceClose = React.useCallback(() => {
    setOpen(false);
    setClickOpen(false);
  }, [setOpen]);
  React.useEffect(() => {
    if (!previewShowing) return;
    claimPreviewSlot(forceClose, insidePreviewPopup);
    return () => releasePreviewSlot(forceClose);
  }, [previewShowing, insidePreviewPopup, forceClose]);

  const holdContext = React.useMemo(() => ({ hold, release }), [hold, release]);

  const hoverHandlers = canHover ? triggerHandlers : undefined;
  const needsConfirmation = !!confirmOnClick && !disabled && !!onClick;

  const handleClick = React.useMemo(() => {
    if (disabled || !onClick) {
      if (!clickToPreview) return undefined;
      return () => {
        setOpen(false);
        if (dismissedByChipRef.current) {
          dismissedByChipRef.current = false;
          return;
        }
        setClickOpen((prev) => !prev);
      };
    }
    if (needsConfirmation) {
      return () => {
        setOpen(false);
        setConfirmOpen(true);
      };
    }
    return (e: React.MouseEvent<HTMLElement>) => {
      setOpen(false);
      onClick(e);
    };
  }, [disabled, onClick, needsConfirmation, clickToPreview, setOpen]);

  // The trigger is deliberately NOT a native button element: chips nest
  // inside clickable rows and anchors, and a button cannot nest inside
  // another button. When interactive it carries button semantics + keyboard
  // activation instead.
  const interactiveProps = handleClick
    ? {
        onClick: handleClick,
        role: "button" as const,
        tabIndex: 0,
        onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => {
          if (e.key !== "Enter" && e.key !== " ") return;
          e.preventDefault();
          handleClick(e as unknown as React.MouseEvent<HTMLElement>);
        },
      }
    : undefined;

  const ghostActive = !!handleClick && ghost;

  // No TextClassContext in the DOM world: the tint goes on the trigger element
  // and descendant text inherits `color`. A `muted` chip is already the default
  // foreground, so it stays untinted rather than restating it.
  const tinted =
    !!color && (as === "text" || (variant === "chip" && color !== "muted"));
  const tintProps = tinted ? { "data-color": color } : undefined;
  const labelTextClass = tinted ? "text-c" : undefined;

  const trigger =
    as === "text" ? (
      <Text
        ref={triggerRef as React.Ref<HTMLSpanElement>}
        data-slot="entity-chip"
        {...tintProps}
        {...interactiveProps}
        onPointerEnter={hoverHandlers?.onPointerEnter}
        onPointerLeave={hoverHandlers?.onPointerLeave}
        onPointerMove={hoverHandlers?.onPointerMove}
        className={cn(
          interactiveProps && "select-none",
          ghostActive && GHOST_TEXT_CLASSES,
          labelTextClass,
        )}
      >
        {children}
      </Text>
    ) : size ? (
      // Sized chip: borrow Button's exact ghost geometry so it lines up with
      // adjacent buttons — variant classes on a Box, never a native button
      // element. `shrink min-w-0` overrides the variant's `shrink-0` so the
      // label can truncate in dense rows.
      <Box minWidth={0}
        ref={triggerRef as React.Ref<HTMLDivElement>}
        data-slot="entity-chip"
        fill={fill}
        {...tintProps}
        {...interactiveProps}
        onPointerEnter={hoverHandlers?.onPointerEnter}
        onPointerLeave={hoverHandlers?.onPointerLeave}
        onPointerMove={hoverHandlers?.onPointerMove}
        className={cn(
          buttonVariants({ variant: "ghost", size }),
          "shrink",
          labelTextClass,
        )}
      >
        {children}
      </Box>
    ) : (
      <Box
        ref={triggerRef as React.Ref<HTMLDivElement>}
        data-slot="entity-chip"
        direction="row"
        align="center"
        gap={visualGap}
        shrink
        minWidth={0}
        fill={fill}
        {...tintProps}
        {...interactiveProps}
        onPointerEnter={hoverHandlers?.onPointerEnter}
        onPointerLeave={hoverHandlers?.onPointerLeave}
        onPointerMove={hoverHandlers?.onPointerMove}
        className={cn(
          entityChipVariants({ variant, ghost: ghostActive }),
          labelTextClass,
        )}
      >
        {children}
      </Box>
    );

  // Lazy mount: no Popover parts until first hover — or until a click
  // requests confirmation or the preview (touch devices never hover, so both
  // must mount on their own).
  const popoverMounted = confirmOpen || clickOpen || (canHover && hasHovered);

  // The returned tree keeps ONE shape for the life of the chip: the popover
  // mounts as a SIBLING of the trigger behind an always-present provider.
  // Swapping the root element type instead remounts the trigger's DOM node
  // mid-hover; the browser then has no target for the pointerleave that would
  // cancel the armed open timer, and the preview opens under a pointer that
  // already left — with nothing ever closing it.
  return (
    <HoverIntentHoldContext.Provider
      // A chip that cannot hover passes its ancestor's context through, so a
      // descendant preview still holds the nearest hover-capable ancestor.
      value={canHover ? holdContext : parentHold}
    >
      {trigger}
      {popoverMounted ? (
        <Popover
          open={open || confirmOpen || clickOpen}
          // Only honor closes from Base UI; opens come from the hover timers /
          // click / confirm request, so a plain click doesn't toggle the
          // preview over the click action.
          onOpenChange={(next, details) => {
            if (!next) {
              const target = details?.event?.target;
              dismissedByChipRef.current =
                target instanceof Node &&
                !!triggerRef.current?.contains(target);
              setOpen(false);
              setConfirmOpen(false);
              setClickOpen(false);
            }
          }}
        >
          {/* Anchor, not Trigger: a Trigger renders a button that swallows
              clicks and is invalid when the chip sits inside another clickable
              element — the trigger ref anchors the positioner instead. */}
          <PopoverPopup
            side={side}
            align={align}
            anchor={triggerRef}
            padding="none"
            {...POPUP_EVENT_SEAL}
            onPointerEnter={contentHandlers.onPointerEnter}
            onPointerLeave={contentHandlers.onPointerLeave}
          >
            <InsidePreviewPopupContext.Provider value={true}>
              {confirmOpen && confirmOnClick ? (
                <Box padding="sm" gap="sm" maxWidth={280}>
                  <Text size="sm">{confirmOnClick.message}</Text>
                  <Box direction="row" justify="end" gap="sm">
                    <Button
                      variant="ghost"
                      size="xs"
                      onClick={() => setConfirmOpen(false)}
                    >
                      {confirmOnClick.cancelLabel ?? "Cancel"}
                    </Button>
                    <Button
                      size="xs"
                      onClick={(e) => {
                        setConfirmOpen(false);
                        onClick?.(e);
                      }}
                    >
                      {confirmOnClick.confirmLabel ?? "Continue"}
                    </Button>
                  </Box>
                </Box>
              ) : open || clickOpen ? (
                renderPreview?.()
              ) : null}
            </InsidePreviewPopupContext.Provider>
          </PopoverPopup>
        </Popover>
      ) : null}
    </HoverIntentHoldContext.Provider>
  );
}

EntityChipShellComponent.displayName = "EntityChipShell";

// Memoized: hundreds of chips render in virtualized tables.
export const EntityChipShell = React.memo(EntityChipShellComponent);
